import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { selectAdminToken } from "../../admin/sessionSlice";
import { apiUrl } from "../../utils/apiUrl";
import { s3ThemeAssetUrl } from "../../utils/themeAssets";
import { clearJury } from "./jurySlice";
import "../../components/stage/stageShell.css";
import "../gallery/gallery.css";
import "./jury.css";

const isVideoFile = (file) => /\.(mp4|mov)$/i.test(file || "");

const PAGE_SIZE = 8;

const REACTIONS = [
  { key: "heart", emoji: "❤️" },
  { key: "smile", emoji: "😄" },
  { key: "clap", emoji: "👏" },
];

const emptyReactions = () => ({ heart: 0, smile: 0, clap: 0 });

export default function JuryVote() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const adminToken = useSelector(selectAdminToken);
  const jury = useSelector((s) => s.jury.jury);
  const themeData = useSelector((s) => s.theme.data);

  const categories = useMemo(() => {
    const names = Array.isArray(themeData?.default_category_name)
      ? themeData.default_category_name
      : [];
    return ["All", ...names.filter((n) => String(n).toUpperCase() !== "ALL")];
  }, [themeData]);

  const [uploads, setUploads] = useState([]);
  const [maxPoints, setMaxPoints] = useState(100);
  const [category, setCategory] = useState("All");
  const [q, setQ] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [scores, setScores] = useState({});
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (!jury?.id) navigate("/jury/login", { replace: true });
  }, [jury, navigate]);

  useEffect(() => {
    if (!preview) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setPreview(null);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [preview]);

  const loadGallery = useCallback(async () => {
    if (!adminToken || !jury?.id) return;
    setLoading(true);
    try {
      const resp = await fetch(apiUrl("/juryGallery"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({
          juryMemberId: jury.id,
          category,
          q,
        }),
      });
      const json = await resp.json();
      if (resp.ok && json.success) {
        const list = Array.isArray(json.uploads) ? json.uploads : [];
        setUploads(list);
        setMaxPoints(json.maxPoints || 100);
        const map = {};
        list.forEach((u) => {
          if (u.myScore != null) map[u.id] = String(u.myScore);
        });
        setScores(map);
      } else {
        setUploads([]);
      }
    } catch (e) {
      console.error(e);
      setUploads([]);
    } finally {
      setLoading(false);
    }
  }, [adminToken, category, jury, q]);

  useEffect(() => {
    loadGallery();
  }, [loadGallery]);

  useEffect(() => {
    setPage(1);
  }, [category, q]);

  const totalPages = Math.max(1, Math.ceil(uploads.length / PAGE_SIZE));
  const pageItems = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return uploads.slice(start, start + PAGE_SIZE);
  }, [uploads, page]);

  const saveScore = async (gameId) => {
    const raw = scores[gameId];
    const pts = parseInt(String(raw ?? ""), 10);
    if (!Number.isFinite(pts) || pts < 0 || pts > maxPoints) {
      Swal.fire("Invalid", `Enter a score between 0 and ${maxPoints}.`, "warning");
      return;
    }
    setSavingId(gameId);
    try {
      const resp = await fetch(apiUrl("/juryScore"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({
          juryMemberId: jury.id,
          gameId,
          points: pts,
        }),
      });
      const json = await resp.json();
      if (!resp.ok || !json.success) throw new Error(json.error || "Save failed");
      setUploads((prev) =>
        prev.map((u) => (u.id === gameId ? { ...u, myScore: pts } : u))
      );
      Swal.fire({ toast: true, position: "top-end", icon: "success", title: "Score saved", showConfirmButton: false, timer: 1200 });
    } catch (e) {
      Swal.fire("Error", e.message || "Save failed", "error");
    } finally {
      setSavingId(null);
    }
  };

  if (!jury?.id) return null;

  return (
    <div className="stage-shell jury-vote-page">
      <div className="stage-inner">
        <p className="jury-eyebrow">Voting is now open</p>
        <h1 className="stage-title" style={{ color: "#e25569" }}>
          Jury vote
        </h1>
        <p className="stage-subtitle">
          Welcome {jury.name || jury.email}. Score each entry (max {maxPoints}).
        </p>

        <div className="gal-filters" style={{ justifyContent: "center", marginBottom: 20 }}>
          <div className="gal-search">
            <span aria-hidden>🔍</span>
            <input
              type="search"
              placeholder="Search talents..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") setQ(searchInput.trim());
              }}
            />
            <button type="button" onClick={() => setQ(searchInput.trim())}>
              Go
            </button>
          </div>
          <label className="gal-cat-filter">
            <span>Select category</span>
            <select value={category} onChange={(e) => setCategory(e.target.value)}>
              {categories.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="add-btn"
            onClick={() => {
              dispatch(clearJury());
              navigate("/jury/login");
            }}
          >
            Log out
          </button>
        </div>

        {loading ? (
          <div className="quiz-loader-container">
            <div className="quiz-loader" />
          </div>
        ) : pageItems.length === 0 ? (
          <p className="gal-empty">No uploads to score.</p>
        ) : (
          <div className="gal-grid">
            {pageItems.map((item, idx) => {
              const counts = {
                ...emptyReactions(),
                ...(item.reactionCounts || {}),
              };
              return (
              <article key={item.galleryKey || item.id} className="gal-card jury-card">
                <span className="gal-tag">
                  {String((page - 1) * PAGE_SIZE + idx + 1).padStart(2, "0")}
                </span>
                <button
                  type="button"
                  className="gal-media"
                  onClick={() => setPreview(item)}
                  aria-label={`View ${item.name || "entry"}`}
                >
                  {isVideoFile(item.uploadFile) ? (
                    <video
                      src={s3ThemeAssetUrl(item.uploadFile)}
                      muted
                      playsInline
                      preload="metadata"
                    />
                  ) : (
                    <img
                      src={s3ThemeAssetUrl(item.uploadFile)}
                      alt={item.name || "Entry"}
                      referrerPolicy="no-referrer"
                    />
                  )}
                </button>
                <h3>{item.name || "Anonymous"}</h3>
                <p className="gal-msg">{item.category || item.message || ""}</p>
                <div className="gal-reactions jury-reactions">
                  {REACTIONS.map((r) => (
                    <span key={r.key} className="gal-react-pill jury-react-pill">
                      <span className="jury-react-emoji" aria-hidden>
                        {r.emoji}
                      </span>
                      <span className="jury-react-count">
                        {Number(counts[r.key]) || 0}
                      </span>
                    </span>
                  ))}
                </div>
                {item.weightedScore != null ? (
                  <p className="jury-result-score">🏅 {item.weightedScore}</p>
                ) : null}
                <div className="jury-score-box">
                  {(item.juryScores || []).length > 0 ? (
                    <div className="jury-scores-list">
                      {(item.juryScores || []).map((js) => {
                        const isMe = js.isMe || js.juryMemberId === jury.id;
                        const alreadyScored =
                          isMe && (item.myScore != null || js.points != null);
                        return (
                          <div
                            key={js.juryMemberId}
                            className={`jury-score-row${isMe ? " jury-score-row--me" : ""}`}
                          >
                            <label>{js.name}</label>
                            {isMe ? (
                              <>
                                <input
                                  type="number"
                                  min={0}
                                  max={maxPoints}
                                  value={scores[item.id] ?? ""}
                                  readOnly={alreadyScored}
                                  disabled={alreadyScored || savingId === item.id}
                                  onChange={(e) =>
                                    setScores((prev) => ({
                                      ...prev,
                                      [item.id]: e.target.value,
                                    }))
                                  }
                                />
                                <button
                                  type="button"
                                  className="stage-cta jury-save-btn"
                                  disabled={alreadyScored || savingId === item.id}
                                  onClick={() => saveScore(item.id)}
                                >
                                  {alreadyScored
                                    ? "Scored"
                                    : savingId === item.id
                                      ? "Saving…"
                                      : "Save"}
                                </button>
                              </>
                            ) : (
                              <div className="jury-score-readonly">
                                {js.points != null ? js.points : "—"}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <>
                      <label>{jury.name || jury.email || "Your score"}</label>
                      <input
                        type="number"
                        min={0}
                        max={maxPoints}
                        value={scores[item.id] ?? ""}
                        readOnly={item.myScore != null}
                        disabled={item.myScore != null || savingId === item.id}
                        onChange={(e) =>
                          setScores((prev) => ({
                            ...prev,
                            [item.id]: e.target.value,
                          }))
                        }
                      />
                      <button
                        type="button"
                        className="stage-cta jury-save-btn"
                        disabled={item.myScore != null || savingId === item.id}
                        onClick={() => saveScore(item.id)}
                      >
                        {item.myScore != null
                          ? "Scored"
                          : savingId === item.id
                            ? "Saving…"
                            : "Save "}
                      </button>
                    </>
                  )}
                </div>
              </article>
              );
            })}
          </div>
        )}

        {totalPages > 1 && (
          <div className="gal-pager">
            <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              ‹
            </button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => (
              <button
                key={i + 1}
                type="button"
                className={page === i + 1 ? "active" : ""}
                onClick={() => setPage(i + 1)}
              >
                {i + 1}
              </button>
            ))}
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              ›
            </button>
          </div>
        )}
      </div>

      {preview ? (
        <div
          className="gal-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label="Talent preview"
          onClick={() => setPreview(null)}
        >
          <div
            className="gal-lightbox-panel"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="gal-lightbox-close"
              onClick={() => setPreview(null)}
              aria-label="Close"
            >
              ×
            </button>
            <div className="gal-lightbox-media">
              {isVideoFile(preview.uploadFile) ? (
                <video
                  src={s3ThemeAssetUrl(preview.uploadFile)}
                  controls
                  playsInline
                  autoPlay
                />
              ) : (
                <img
                  src={s3ThemeAssetUrl(preview.uploadFile)}
                  alt={preview.name || "Entry"}
                  referrerPolicy="no-referrer"
                />
              )}
            </div>
            <div className="gal-lightbox-meta">
              {preview.category ? (
                <span className="gal-lightbox-tag">{preview.category}</span>
              ) : null}
              <h3>{preview.name || "Anonymous"}</h3>
              {preview.message ? (
                <p className="gal-lightbox-title">{preview.message}</p>
              ) : null}
              {preview.descriptions ? (
                <p className="gal-lightbox-desc">{preview.descriptions}</p>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
      {/* <div className="stage-platform" aria-hidden /> */}
    </div>
  );
}
